import { Controller } from "@hotwired/stimulus"
import db from "../field_db"
import { logEvent, logError } from "../field_logger"
import { setCachedResponseStatus } from "../field_sync_shared"
import { canOpenSurvey } from "../lib/field_start_rule"
import { recordLedgerEntry, ACTIONS } from "../field_response_ledger"

// Handles the Start button on response cards.
//
// Mounted on the button_to <form> (card #3827) -- turbo:submit-end is
// dispatched on the form and bubbles up, so this never ran when it sat on the
// <button>.
//
// Online success: writes "started" into the response's cached_responses row,
// so the offline shell and Today tab don't keep showing it as scheduled.
//
// The Start action is a POST to the server. When offline, the request fails.
// This controller catches the failure, checks whether the response is
// pre-cached in IndexedDB, and either navigates to the offline-app shell
// (where the response can be started) or shows an inline message if the
// response was never synced to this device.
//
// Values:
//   responseUrl -- the response show path (used for online success logging)
//   responseId  -- the server Response#id, used to check IndexedDB cache

export default class extends Controller {
  static values = { responseUrl: String, responseId: Number }

  connect() {
    this._onSubmitEnd = this._handleSubmitEnd.bind(this)
    this.element.addEventListener("turbo:submit-end", this._onSubmitEnd)
  }

  disconnect() {
    this.element.removeEventListener("turbo:submit-end", this._onSubmitEnd)
  }

  async _handleSubmitEnd(event) {
    // Card #4688 -- one ledger entry for this server write regardless of
    // outcome. The POST always redirects (Field::ResponsesController#start),
    // so fetch's default redirect: "follow" means the response JS sees here
    // is the LANDED page's, not this write's own -- requestId is left null
    // rather than attribute the landed page's own request id to this write
    // (see field_response_ledger.js's header comment).
    const httpResponse = event.detail?.fetchResponse?.response || null
    // One cache read serves both the ledger's localStatusBefore and (on
    // failure, below) the existing offline-recovery check.
    const cached = await db.cached_responses
      .where("responseId").equals(this.responseIdValue)
      .first()
      .catch(() => null)
    recordLedgerEntry(this.responseIdValue, {
      action:             ACTIONS.START,
      localStatusBefore:  cached?.status ?? null,
      localStatusAfter:   event.detail.success ? "started" : null,
      httpStatus:         httpResponse?.status ?? null,
      serverStatus:       httpResponse?.headers?.get?.("X-WIN-Response-Status") || null,
      requestId:          null
    }).catch(() => {})

    if (event.detail.success) {
      logEvent("response", "Response started", { url: this.responseUrlValue }).catch(() => {})
      try {
        const updated = await setCachedResponseStatus(db, this.responseIdValue, "started")
        if (!updated) {
          logEvent("response", "Response started, no cached copy to update", { responseId: this.responseIdValue }).catch(() => {})
        }
      } catch (error) {
        logError("response", "Cached response status write failed", {
          responseId: this.responseIdValue, status: "started", message: error?.message || null
        }).catch(() => {})
      }
      return
    }

    // POST failed -- check if this response is pre-cached in IndexedDB
    // (reuses the `cached` row read above for the ledger entry).

    // Card #4152: the same Start rule as the server -- only open the cached
    // survey when the cached copy is scheduled for today or earlier, or already
    // started. A stale or unscheduled copy must not bypass the rule.
    if (cached && canOpenSurvey(cached)) {
      logEvent("response", "Response start failed offline, navigating to cached response page", { responseId: this.responseIdValue }).catch(() => {})
      Turbo.visit(this.responseUrlValue)
    } else if (cached) {
      logEvent("response", "Response start failed offline, cached copy not startable", { responseId: this.responseIdValue, status: cached.status }).catch(() => {})
      this._showMessage("Not ready to start offline &mdash; sync when connected")
    } else {
      logEvent("response", "Response start failed offline, not cached", { responseId: this.responseIdValue }).catch(() => {})
      this._showNotCachedMessage()
    }
  }

  _showNotCachedMessage() {
    this._showMessage("Not available offline &mdash; sync when connected")
  }

  _showMessage(html) {
    // Replace the Start button (the form's only child) with an inline message.
    // The form stays, so the controller stays connected; the user can dismiss
    // by navigating away, and on reconnect the card re-renders normally.
    this.element.innerHTML = `
      <div class="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-xl border border-slate-100">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 8.689c0-.864.933-1.406 1.683-.977l7.108 4.061a1.125 1.125 0 0 1 0 1.954l-7.108 4.061A1.125 1.125 0 0 1 3 16.811V8.69ZM12.75 8.689c0-.864.933-1.406 1.683-.977l7.108 4.061a1.125 1.125 0 0 1 0 1.954l-7.108 4.061a1.125 1.125 0 0 1-1.683-.977V8.69Z" />
        </svg>
        <p class="text-xs text-slate-500">${html}</p>
      </div>
    `
  }
}
